import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { signIn } from '@/lib/auth-client'
import {
	PortalCentered,
	portalButtonOutline,
	portalHeading,
	portalLabel,
	portalPanel,
} from '@/routes/appointments/portal-shell'

// Staff sign in with Google only. The email+password path was a dev-only
// fallback; it is intentionally gone so the login route offers a single,
// unambiguous way in.
export function LoginPage() {
	const location = useLocation()
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	const from =
		(location.state as { from?: { pathname: string } } | null)?.from
			?.pathname ?? '/'

	const onGoogleSignIn = async () => {
		setError(null)
		setIsLoading(true)
		try {
			// callbackURL must be ABSOLUTE to the client origin. BetterAuth resolves
			// a relative path against BETTER_AUTH_URL (the API on :4000), which has
			// no SPA routes — the browser would land on :4000/ and 404. Sending the
			// full client URL routes the post-login redirect back to the web app.
			// On success BetterAuth redirects to callbackURL, so control does not
			// return here; only reset the pending state if the call itself throws.
			await signIn.social({
				provider: 'google',
				callbackURL: `${window.location.origin}${from}`,
			})
		} catch {
			setError('Přihlášení přes Google se nezdařilo. Zkuste to prosím znovu.')
			setIsLoading(false)
		}
	}

	return (
		<PortalCentered>
			<div className={`${portalPanel} w-full max-w-md p-8 sm:p-10`}>
				<div className="mb-8 text-center">
					{/* Text rendition of the site logo */}
					<p className={`${portalHeading} text-3xl leading-none`}>Josenika</p>
					<span className={`${portalLabel} mt-5 block`}>Interní systém</span>
					<h1 className={`${portalHeading} mt-2 text-[28px] leading-tight`}>
						Přihlášení
					</h1>
					<div className="mx-auto mt-4 h-[2px] w-[55px] bg-[#d7ad6c]" />
					<p className="mt-4 text-base">Přihlaste se pomocí účtu Google</p>
				</div>

				<Button
					type="button"
					className={`${portalButtonOutline} h-11 w-full`}
					onClick={onGoogleSignIn}
					disabled={isLoading}
				>
					<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
						<path
							d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
							fill="#4285F4"
						/>
						<path
							d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
							fill="#34A853"
						/>
						<path
							d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"
							fill="#FBBC05"
						/>
						<path
							d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
							fill="#EA4335"
						/>
					</svg>
					Přihlásit se přes Google
				</Button>

				{error && <p className="mt-4 text-destructive text-sm">{error}</p>}
			</div>
			<p className="text-sm text-[#919194]">
				Přístup pouze pro zaměstnance ordinace
			</p>
		</PortalCentered>
	)
}
